'use client';

import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  username: string;
  password: string;
  confirmPassword: string;
  referralCode: string;
  binaryPlacement: 'extreme_left' | 'extreme_right';
  termsAccepted: boolean;
  privacyAccepted: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

/**
 * Finds the bottom-most available slot in the sponsor's binary tree
 * for the given direction (L = extreme left, R = extreme right).
 *
 * Traversal: starting from sponsorRnaId, keep following the child whose
 * binary_position matches the desired direction (L or R) until no such
 * child exists. The last node found is the parent for the new account.
 *
 * Returns { parentId, parentPosition } where:
 *   - parentId: the richesse_network_accounts.id of the node that will
 *               be the new account's binary_id *   - parentPosition:'L' | 'R' — the position the new account occupies
 *                     under that parent (always the chosen direction)
 */
async function findSpilloverSlot(
  supabase: ReturnType<typeof createClient>,
  sponsorRnaId: number,
  direction: 'L' | 'R'
): Promise<{ parentId: number; parentPosition: 'L' | 'R' }> {
  let currentId = sponsorRnaId;
  const MAX_DEPTH = 500;

  for (let depth = 0; depth < MAX_DEPTH; depth++) {
    // Find a direct child of currentId whose binary_position === direction
    // and whose binary_id === currentId (meaning they are placed directly under currentId)
    const { data: child } = await supabase
      .from('richesse_network_accounts')
      .select('id')
      .eq('binary_id', currentId)
      .eq('binary_position', direction)
      .maybeSingle();

    if (!child) {
      // No child in this direction — currentId is the bottom slot
      return { parentId: currentId, parentPosition: direction };
    }

    // Move deeper
    currentId = child.id;
  }

  // Fallback: return the last found node
  return { parentId: currentId, parentPosition: direction };
}

/**
 * Same as findSpilloverSlot but for network_accounts table.
 */
async function findSpilloverSlotNA(
  supabase: ReturnType<typeof createClient>,
  sponsorNaId: number,
  direction: 'L' | 'R'
): Promise<{ parentId: number; parentPosition: 'L' | 'R' }> {
  let currentId = sponsorNaId;
  const MAX_DEPTH = 500;

  for (let depth = 0; depth < MAX_DEPTH; depth++) {
    const { data: child } = await supabase
      .from('network_accounts')
      .select('id')
      .eq('binary_id', currentId)
      .eq('binary_position', direction)
      .maybeSingle();

    if (!child) {
      return { parentId: currentId, parentPosition: direction };
    }

    currentId = child.id;
  }

  return { parentId: currentId, parentPosition: direction };
}

export default function RegisterForm({ onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [binaryPlacement, setBinaryPlacement] = useState<'extreme_left' | 'extreme_right'>('extreme_left');
  const router = useRouter();
  const supabase = createClient();
  // Ref-based guard to prevent double submission (state updates are async)
  const isSubmittingRef = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: { referralCode: '', binaryPlacement: 'extreme_left' },
  });

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    // Guard against double submission
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // 0. Check for duplicate username (case-insensitive — stored as uppercase)
      const { data: existingUsername, error: usernameCheckError } = await supabase
        .from('richesse_network_accounts')
        .select('id')
        .eq('username', data.username.toUpperCase())
        .maybeSingle();

      if (usernameCheckError) {
        // non-fatal
      } else if (existingUsername) {
        toast.error('Username already taken', {
          description: `The username "${data.username.toUpperCase()}" is already in use. Please choose a different username.`,
        });
        setLoading(false);
        return;
      }

      // 0b. Validate referral code BEFORE creating any user (now required)
      let sponsorId = 0;
      let sponsorUsername: string | null = null;
      let sponsorName: string | null = null;
      let sponsorEmail: string | null = null;
      let naSponsorId = 0;

      const { data: sponsorRNA, error: sponsorLookupError } = await supabase
        .from('richesse_network_accounts')
        .select('id, username, name, email')
        .eq('username', data.referralCode.toUpperCase())
        .maybeSingle();

      if (sponsorLookupError) {
        toast.error('Referral code lookup failed', {
          description: `Could not verify the referral code. Please try again.`,
        });
        setLoading(false);
        return;
      }

      if (!sponsorRNA) {
        toast.error('Invalid referral code', {
          description: `The referral code "${data.referralCode}" does not exist. Please check and try again.`,
        });
        setLoading(false);
        return;
      }

      sponsorId = sponsorRNA.id;
      sponsorUsername = sponsorRNA.username;
      sponsorName = sponsorRNA.name;
      sponsorEmail = sponsorRNA.email;

      const { data: sponsorNARow } = await supabase
        .from('network_accounts')
        .select('id')
        .eq('username', sponsorRNA.username)
        .maybeSingle();
      naSponsorId = sponsorNARow?.id ?? 0;

      // Determine binary direction
      const binaryDir: 'L' | 'R' = binaryPlacement === 'extreme_left' ? 'L' : 'R';

      // 0c. Find the spillover slot for richesse_network_accounts
      const rnaSlot = await findSpilloverSlot(supabase, sponsorId, binaryDir);
      const rnaBinaryParentId = rnaSlot.parentId;
      const binaryPos = rnaSlot.parentPosition; // always === binaryDir

      // 0d. Find the spillover slot for network_accounts (mirror)
      let naSlot: { parentId: number; parentPosition: 'L' | 'R' } | null = null;
      if (naSponsorId) {
        naSlot = await findSpilloverSlotNA(supabase, naSponsorId, binaryDir);
      }
      const naBinaryParentId = naSlot ? naSlot.parentId : (naSponsorId || sponsorId || null);

      // 1. Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: `${data.firstName} ${data.lastName}`,
            first_name: data.firstName,
            last_name: data.lastName,
            username: data.username.toUpperCase(),
            phone: data.mobile,
            referred_by: data.referralCode.toUpperCase(),
          },
          emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
        },
      });

      if (authError) {
        const errCode = (authError as any).code as string | undefined;
        if (
          authError.status === 422 ||
          errCode === 'user_already_exists' ||
          /user already registered/i.test(authError.message)
        ) {
          toast.error('Email already registered', {
            description: `An account with "${data.email}" already exists. Please sign in instead.`,
          });
          setLoading(false);
          return;
        }

        throw new Error(`[Auth SignUp] ${authError.message} (status: ${authError.status ?? 'n/a'})`);
      }
      if (!authData.user) {
        throw new Error('[Auth SignUp] Registration failed — no user returned. Please try again.');
      }

      if (!authData.user.identities || authData.user.identities.length === 0) {
        toast.error('Email already registered', {
          description: `An account with "${data.email}" already exists. Please sign in instead.`,
        });
        setLoading(false);
        return;
      }

      // 2. Ensure we have an active session
      let session = authData.session;
      if (!session) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        });
        if (signInError) {
          throw new Error(`[Auth SignIn] ${signInError.message} (status: ${signInError.status ?? 'n/a'})`);
        }
        session = signInData.session;
      }

      if (!session) {
        throw new Error('[Auth Session] Could not establish session. Please try logging in manually.');
      }

      const userId = authData.user.id;
      const fullName = `${data.firstName} ${data.lastName}`;

      // 2b. Fetch user_profiles.user_number (set by trigger on auth user creation)
      let profileUserNumber: number | null = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        const { data: profileRow, error: profileFetchError } = await supabase
          .from('user_profiles')
          .select('user_number')
          .eq('id', userId)
          .maybeSingle();
        if (profileRow?.user_number) {
          profileUserNumber = profileRow.user_number;
          break;
        }
        // Wait briefly for trigger to fire
        await new Promise((r) => setTimeout(r, 300));
      }

      // 3. Update user_profiles
      const { error: profileUpdateError } = await supabase
        .from('user_profiles')
        .update({
          username: data.username.toUpperCase(),
          phone: data.mobile,
          referred_by: data.referralCode.toUpperCase(),
        })
        .eq('id', userId);
      if (profileUpdateError) {
        if (isSchemaError(profileUpdateError)) {
          throw new Error(`[user_profiles update] ${profileUpdateError.message} (code: ${profileUpdateError.code})`);
        }
      }

      // 4. Upsert wallets
      const { error: walletError } = await supabase.from('wallets').upsert(
        { user_id: userId, balance: 0, currency: 'PHP' },
        { onConflict: 'user_id' }
      );
      if (walletError && isSchemaError(walletError)) {
        throw new Error(`[wallets upsert] ${walletError.message} (code: ${walletError.code})`);
      }

      // 5. Build sponsor chain lists for richesse_network_accounts
      // Walk up from the spillover parent to build the binary chain
      let rnaSponsorLevel = 0;
      let rnaSponsorList: string | null = null;
      let rnaBinaryLevel: number | null = null;
      let rnaBinaryList: string | null = null;
      let rnaChainIds: number[] = [];
      let rnaChainPositions: string[] = [];
      let rnaChainBinaryIds: (number | null)[] = [];

      // Walk up from rnaBinaryParentId to build the ancestor chain
      {
        let curRnaId: number | null = rnaBinaryParentId;
        const tempChainIds: number[] = [];
        const tempChainPositions: string[] = [];
        const tempChainBinaryIds: (number | null)[] = [];

        while (curRnaId && curRnaId !== 0 && tempChainIds.length <= 200) {
          const { data: rnaParentRow } = await supabase
            .from('richesse_network_accounts')
            .select('sponsor_id, binary_position, binary_id')
            .eq('id', curRnaId)
            .maybeSingle();
          tempChainIds.push(curRnaId);
          tempChainPositions.push(rnaParentRow?.binary_position ?? 'L');
          tempChainBinaryIds.push(rnaParentRow?.binary_id ?? null);
          curRnaId = rnaParentRow?.binary_id ?? null;
        }
        tempChainIds.reverse();
        tempChainPositions.reverse();
        tempChainBinaryIds.reverse();
        rnaChainIds = tempChainIds;
        rnaChainPositions = tempChainPositions;
        rnaChainBinaryIds = tempChainBinaryIds;
      }

      // Build sponsor chain (upline) for unilevel
      {
        let curRnaId: number | null = sponsorId;
        const tempSponsorIds: number[] = [];
        while (curRnaId && curRnaId !== 0 && tempSponsorIds.length <= 200) {
          const { data: rnaParentRow } = await supabase
            .from('richesse_network_accounts')
            .select('sponsor_id')
            .eq('id', curRnaId)
            .maybeSingle();
          tempSponsorIds.push(curRnaId);
          curRnaId = rnaParentRow?.sponsor_id ?? null;
        }
        tempSponsorIds.reverse();
        rnaSponsorLevel = tempSponsorIds.length;
        rnaSponsorList = ' 0 ' + tempSponsorIds.join(' ') + ' ';
      }

      rnaBinaryLevel = rnaChainIds.length + 1; // new account is one level below the parent
      const rnaBinaryEntries = rnaChainIds.map((id, i) => `${rnaChainBinaryIds[i] ?? id}x${rnaChainPositions[i]}`);
      // Append the parent entry (the spillover parent) with the chosen direction
      rnaBinaryEntries.push(`${rnaBinaryParentId}x${binaryPos}`);
      rnaBinaryList = ' ' + rnaBinaryEntries.join(' ') + ' ';

      // 6. Build sponsor chain lists for network_accounts (mirror)
      let chainIds: number[] = [];
      let chainPositions: string[] = [];
      let chainBinaryIds: (number | null)[] = [];
      let naSponsorLevel = 0;
      let naSponsorList: string | null = null;
      let naBinaryLevel: number | null = null;
      let naBinaryListStr: string | null = null;

      if (naBinaryParentId) {
        let curId: number | null = naBinaryParentId as number;
        const tempChainIds: number[] = [];
        const tempChainPositions: string[] = [];
        const tempChainBinaryIds: (number | null)[] = [];

        while (curId && curId !== 0 && tempChainIds.length <= 200) {
          const { data: parentRow } = await supabase
            .from('network_accounts')
            .select('sponsor_id, binary_position, binary_id')
            .eq('id', curId)
            .maybeSingle();
          tempChainIds.push(curId);
          tempChainPositions.push(parentRow?.binary_position ?? 'L');
          tempChainBinaryIds.push(parentRow?.binary_id ?? null);
          curId = parentRow?.binary_id ?? null;
        }
        tempChainIds.reverse();
        tempChainPositions.reverse();
        tempChainBinaryIds.reverse();
        chainIds = tempChainIds;
        chainPositions = tempChainPositions;
        chainBinaryIds = tempChainBinaryIds;
      }

      if (naSponsorId) {
        let curId: number | null = naSponsorId;
        const tempSponsorIds: number[] = [];
        while (curId && curId !== 0 && tempSponsorIds.length <= 200) {
          const { data: parentRow } = await supabase
            .from('network_accounts')
            .select('sponsor_id')
            .eq('id', curId)
            .maybeSingle();
          tempSponsorIds.push(curId);
          curId = parentRow?.sponsor_id ?? null;
        }
        tempSponsorIds.reverse();
        naSponsorLevel = tempSponsorIds.length;
        naSponsorList = ' 0 ' + tempSponsorIds.join(' ') + ' ';
      } else {
        naSponsorLevel = rnaSponsorLevel;
        naSponsorList = rnaSponsorList;
      }

      naBinaryLevel = chainIds.length + 1;
      const naBinaryListEntries = chainIds.map((id, i) => `${chainBinaryIds[i] ?? id}x${chainPositions[i]}`);
      naBinaryListEntries.push(`${naBinaryParentId}x${binaryPos}`);
      naBinaryListStr = ' ' + naBinaryListEntries.join(' ') + ' ';

      // 7. Insert richesse_network_accounts
      const { data: rnaData, error: rnaError } = await supabase
        .from('richesse_network_accounts')
        .insert({
          ...(profileUserNumber ? { user_number: profileUserNumber } : {}),
          user_id: userId,
          name: fullName,
          username: data.username.toUpperCase(),
          email: data.email,
          sponsor_id: sponsorId,
          sponsor: sponsorUsername,
          sponsor_name: sponsorName,
          sponsor_email: sponsorEmail,
          sponsor_level: rnaSponsorLevel || null,
          sponsor_list: rnaSponsorList,
          binary_id: rnaBinaryParentId,
          binary_level: rnaBinaryLevel,
          binary_position: binaryPos,
          binary_list: rnaBinaryList,
          binary_status: 'Inactive',
          package_type: null,
          package_activated_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('id, user_number')
        .single();
      if (rnaError) {
        throw new Error(`[richesse_network_accounts insert] ${rnaError.message} (code: ${rnaError.code}${rnaError.details ? ', details: ' + rnaError.details : ''})`);
      }

      const rnaId = rnaData?.id;
      const rnaUserNumber = rnaData?.user_number;

      // Append new registrant's own entry to rnaBinaryList and patch the record
      if (rnaId) {
        const rnaBinaryListFinal = rnaBinaryList
          ? rnaBinaryList.trimEnd() + ` ${rnaId}x${binaryPos} `
          : ` ${rnaId}x${binaryPos} `;
        await supabase
          .from('richesse_network_accounts')
          .update({ binary_list: rnaBinaryListFinal, updated_at: new Date().toISOString() })
          .eq('id', rnaId);
        rnaBinaryList = rnaBinaryListFinal;
      }

      // 8. Insert network_accounts (mirror)
      const { data: existingNA } = await supabase
        .from('network_accounts')
        .select('id, user_number')
        .eq('user_id', userId)
        .maybeSingle();

      let naData: { id: number; user_number: number } | null = existingNA ?? null;

      if (!existingNA) {
        const { data: insertedNA, error: naError } = await supabase
          .from('network_accounts')
          .insert({
            ...(profileUserNumber ? { id: profileUserNumber, user_number: profileUserNumber } : {}),
            user_id: userId,
            name: fullName,
            username: data.username.toUpperCase(),
            email: data.email,
            sponsor_id: naSponsorId || null,
            sponsor: sponsorUsername,
            sponsor_name: sponsorName,
            sponsor_email: sponsorEmail,
            sponsor_level: naSponsorLevel || null,
            sponsor_list: naSponsorList,
            binary_id: naBinaryParentId,
            binary_level: naBinaryLevel,
            binary_list: naBinaryListStr,
            binary_position: binaryPos,
            binary_status: 'Inactive',
            package_type: null,
            package_activated_at: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select('id, user_number')
          .single();
        if (naError) {
          throw new Error(`[network_accounts insert] ${naError.message} (code: ${naError.code}${naError.details ? ', details: ' + naError.details : ''})`);
        }
        naData = insertedNA;
      }

      // Step 8b: Patch binary fields (append new registrant's own entry to naBinaryList)
      const naIdForPatch = naData?.id;
      const naBinaryListFinal = naIdForPatch
        ? (naBinaryListStr ? naBinaryListStr.trimEnd() + ` ${naIdForPatch}x${binaryPos} ` : ` ${naIdForPatch}x${binaryPos} `)
        : naBinaryListStr;
      if (naBinaryParentId || naBinaryLevel || naBinaryListFinal) {
        await supabase
          .from('network_accounts')
          .update({
            binary_id: naBinaryParentId,
            binary_level: naBinaryLevel,
            binary_position: binaryPos,
            binary_list: naBinaryListFinal,
            sponsor_id: naSponsorId || null,
            sponsor_level: naSponsorLevel || null,
            sponsor_list: naSponsorList,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', userId);
      }

      const naId = naData?.id;
      let naUserNumber = naData?.user_number ?? null;

      // If naUserNumber is still null, re-fetch from network_accounts
      if (!naUserNumber && naId) {
        const { data: naRefetch } = await supabase
          .from('network_accounts')
          .select('user_number')
          .eq('id', naId)
          .maybeSingle();
        naUserNumber = naRefetch?.user_number ?? null;
      }
      // Final fallback: use profileUserNumber
      if (!naUserNumber && profileUserNumber) {
        naUserNumber = profileUserNumber;
      }

      // 9. Insert richesse_network_accounts_binaries
      // Build the full ancestor chain from root down to rnaBinaryParentId
      if (rnaId) {
        // Fetch the full binary ancestor chain of rnaBinaryParentId
        const { data: parentBinaries } = await supabase
          .from('richesse_network_accounts_binaries')
          .select('binary_id, binary_level, binary_position')
          .eq('downline_id', rnaBinaryParentId)
          .order('binary_level', { ascending: false });

        const rnabRows: any[] = [];

        // Add all ancestors of the parent
        if (parentBinaries && parentBinaries.length > 0) {
          parentBinaries.forEach((row) => {
            rnabRows.push({
              downline_id: rnaId,
              binary_id: row.binary_id,
              binary_level: (row.binary_level ?? 0) + 1,
              binary_position: row.binary_position,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          });
        }

        // Add the direct parent (rnaBinaryParentId) as the immediate ancestor
        rnabRows.push({
          downline_id: rnaId,
          binary_id: rnaBinaryParentId,
          binary_level: 1,
          binary_position: binaryPos,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (rnabRows.length > 0) {
          const { error: rnabError } = await supabase
            .from('richesse_network_accounts_binaries')
            .insert(rnabRows);
          if (rnabError && isSchemaError(rnabError)) {
            throw new Error(`[richesse_network_accounts_binaries insert] ${rnabError.message} (code: ${rnabError.code})`);
          }
        }
      }

      // 10. Insert richesse_network_accounts_unilevels
      // Build sponsor chain from sponsorId upward
      if (rnaId) {
        const rnauRows: any[] = [];
        let curRnaId: number | null = sponsorId;
        let level = 1;
        while (curRnaId && curRnaId !== 0 && level <= 200) {
          rnauRows.push({
            downline_id: rnaId,
            sponsor_id: curRnaId,
            sponsor_level: level,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
          const { data: rnaParentRow } = await supabase
            .from('richesse_network_accounts')
            .select('sponsor_id')
            .eq('id', curRnaId)
            .maybeSingle();
          curRnaId = rnaParentRow?.sponsor_id ?? null;
          level++;
        }
        if (rnauRows.length > 0) {
          const { error: rnauError } = await supabase
            .from('richesse_network_accounts_unilevels')
            .insert(rnauRows);
          if (rnauError) {
            throw new Error(`[richesse_network_accounts_unilevels insert] ${rnauError.message} (code: ${rnauError.code})`);
          }
        }
      }

      // 11. Insert network_accounts_binaries
      if (naId && naBinaryParentId) {
        const { data: parentNABinaries } = await supabase
          .from('network_accounts_binaries')
          .select('binary_id, binary_level, binary_position')
          .eq('downline_id', naBinaryParentId)
          .order('binary_level', { ascending: false });

        const nabRows: any[] = [];

        if (parentNABinaries && parentNABinaries.length > 0) {
          parentNABinaries.forEach((row) => {
            nabRows.push({
              downline_id: naId,
              binary_id: row.binary_id,
              binary_level: (row.binary_level ?? 0) + 1,
              binary_position: row.binary_position,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          });
        }

        nabRows.push({
          downline_id: naId,
          binary_id: naBinaryParentId,
          binary_level: 1,
          binary_position: binaryPos,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (nabRows.length > 0) {
          const { error: nabError } = await supabase
            .from('network_accounts_binaries')
            .insert(nabRows);
          if (nabError && isSchemaError(nabError)) {
            throw new Error(`[network_accounts_binaries insert] ${nabError.message} (code: ${nabError.code})`);
          }
        }
      }

      // 12. Insert network_accounts_unilevels
      const nauEffectiveSponsorId = naSponsorId || sponsorId || null;
      if (naId && nauEffectiveSponsorId) {
        await supabase.from('network_accounts_unilevels').delete().eq('downline_id', naId);
        const nauRows: any[] = [];
        let curId: number | null = nauEffectiveSponsorId;
        let level = 1;
        while (curId && curId !== 0 && level <= 200) {
          nauRows.push({
            downline_id: naId,
            sponsor_id: curId,
            sponsor_level: level,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
          const { data: parentRow } = await supabase
            .from('network_accounts')
            .select('sponsor_id')
            .eq('id', curId)
            .maybeSingle();
          curId = parentRow?.sponsor_id ?? null;
          level++;
        }
        if (nauRows.length > 0) {
          const { error: nauError } = await supabase
            .from('network_accounts_unilevels')
            .insert(nauRows);
          if (nauError) {
            throw new Error(`[network_accounts_unilevels insert] ${nauError.message} (code: ${nauError.code})`);
          }
        }
      }

      // 13. Insert richesse_network_wallets
      let resolvedRnaUserNumber = rnaUserNumber;
      if (!resolvedRnaUserNumber && rnaId) {
        const { data: rnaRefetch } = await supabase
          .from('richesse_network_accounts')
          .select('user_number')
          .eq('id', rnaId)
          .maybeSingle();
        resolvedRnaUserNumber = rnaRefetch?.user_number ?? null;
      }
      // Final fallback: use profileUserNumber
      if (!resolvedRnaUserNumber && profileUserNumber) {
        resolvedRnaUserNumber = profileUserNumber;
      }
      if (rnaId && resolvedRnaUserNumber) {
        const { error: rnwError } = await supabase.from('richesse_network_wallets').insert({
          user_id: userId,
          user_number: resolvedRnaUserNumber,
          type: 'wallet',
          category: 'main',
          source: rnaId,
          amount: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        if (rnwError) {
          throw new Error(`[richesse_network_wallets insert] ${rnwError.message} (code: ${rnwError.code}${rnwError.details ? ', details: ' + rnwError.details : ''})`);
        }
      }

      // 14. Insert network_wallets
      // Final fallback: use resolvedRnaUserNumber if naUserNumber is still null
      if (!naUserNumber && resolvedRnaUserNumber) {
        naUserNumber = resolvedRnaUserNumber;
      }
      if (naUserNumber) {
        const { error: nwError } = await supabase.from('network_wallets').insert({
          user_id: userId,
          user_number: naUserNumber,
          type: 'credit',
          category: 'registration',
          source: 0,
          amount: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        if (nwError && isSchemaError(nwError)) {
          throw new Error(`[network_wallets insert] ${nwError.message} (code: ${nwError.code})`);
        }
      }

      // 15. Insert network_wallets_cashouts
      if (naUserNumber) {
        const { error: nwcError } = await supabase.from('network_wallets_cashouts').insert({
          user_number: naUserNumber,
          amount: 0,
          tax: 0,
          fee: 0,
          net: 0,
        });
        if (nwcError && isSchemaError(nwcError)) {
          throw new Error(`[network_wallets_cashouts insert] ${nwcError.message} (code: ${nwcError.code})`);
        }
      }

      toast.success('Welcome to Richésse!', {
        description: `Your store at richesse.global/${data.username} is live.`,
      });
      setTimeout(() => router.push('/member-dashboard'), 1000);
    } catch (err: any) {
      toast.error('Registration failed', {
        description: err?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1.5">Create your account</h2>
        <p className="text-sm text-muted-foreground">Your personal Richésse store is ready the moment you join.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Name row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="richesse-label">First Name *</label>
            <input
              type="text"
              autoComplete="given-name"
              placeholder="Maria"
              className={`richesse-input ${errors.firstName ? 'border-danger' : ''}`}
              {...register('firstName', { required: 'Required' })}
            />
            {errors.firstName && <p className="mt-1 text-xs text-danger">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="richesse-label">Last Name *</label>
            <input
              type="text"
              autoComplete="family-name"
              placeholder="Santos"
              className={`richesse-input ${errors.lastName ? 'border-danger' : ''}`}
              {...register('lastName', { required: 'Required' })}
            />
            {errors.lastName && <p className="mt-1 text-xs text-danger">{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="richesse-label">Email Address *</label>
          <input
            type="email"
            autoComplete="email"
            placeholder="maria@example.com"
            className={`richesse-input ${errors.email ? 'border-danger' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
        </div>

        {/* Mobile */}
        <div>
          <label className="richesse-label">Mobile Number *</label>
          <input
            type="tel"
            autoComplete="tel"
            placeholder="+63 917 000 0000"
            className={`richesse-input ${errors.mobile ? 'border-danger' : ''}`}
            {...register('mobile', { required: 'Mobile number is required' })}
          />
          {errors.mobile && <p className="mt-1 text-xs text-danger">{errors.mobile.message}</p>}
        </div>

        {/* Username */}
        <div>
          <label className="richesse-label">Username *</label>
          <p className="text-xs text-muted-foreground mb-1.5">Your store URL: richesse.global/<span className="text-primary">{watch('username') || 'yourname'}</span></p>
          <input
            type="text"
            placeholder="mariasantos"
            className={`richesse-input ${errors.username ? 'border-danger' : ''}`}
            {...register('username', {
              required: 'Username is required',
              minLength: { value: 3, message: 'Minimum 3 characters' },
              pattern: { value: /^[a-zA-Z0-9_]+$/, message: 'Letters, numbers, underscores only' },
            })}
          />
          {errors.username && <p className="mt-1 text-xs text-danger">{errors.username.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="richesse-label">Password *</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Min. 8 characters"
              className={`richesse-input pr-10 ${errors.password ? 'border-danger' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
        </div>

        {/* Confirm password */}
        <div>
          <label className="richesse-label">Confirm Password *</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat password"
              className={`richesse-input pr-10 ${errors.confirmPassword ? 'border-danger' : ''}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: v => v === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
        </div>

        {/* Referral code — now required, no None option */}
        <div>
          <label className="richesse-label">Sponsor / Referral Code *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Icon name="GiftIcon" size={16} />
            </span>
            <input
              type="text"
              placeholder="Enter sponsor's username"
              className={`richesse-input pl-9 ${errors.referralCode ? 'border-danger' : ''}`}
              {...register('referralCode', {
                required: 'Sponsor / Referral Code is required',
                onChange: (e) => {
                  e.target.value = e.target.value.toUpperCase();
                  setValue('referralCode', e.target.value);
                },
              })}
            />
          </div>
          {errors.referralCode && <p className="mt-1 text-xs text-danger">{errors.referralCode.message}</p>}
        </div>

        {/* Binary Placement */}
        <div>
          <label className="richesse-label">Binary Placement</label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <button
              type="button"
              onClick={() => setBinaryPlacement('extreme_left')}
              className={`rounded-2xl border-2 py-4 px-3 text-center transition-all duration-200 ${
                binaryPlacement === 'extreme_left' ?'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30' :'border-border bg-muted/40 hover:bg-muted/70'
              }`}
            >
              <p className={`richesse-label mb-0 ${binaryPlacement === 'extreme_left' ? 'text-yellow-500' : 'text-foreground'}`}>
                Extreme Left
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">End of left-most line</p>
            </button>
            <button
              type="button"
              onClick={() => setBinaryPlacement('extreme_right')}
              className={`rounded-2xl border-2 py-4 px-3 text-center transition-all duration-200 ${
                binaryPlacement === 'extreme_right' ?'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30' :'border-border bg-muted/40 hover:bg-muted/70'
              }`}
            >
              <p className={`richesse-label mb-0 ${binaryPlacement === 'extreme_right' ? 'text-yellow-500' : 'text-foreground'}`}>
                Extreme Right
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">End of right-most line</p>
            </button>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">Auto-places you at the bottom of your sponsor's binary line.</p>
        </div>

        {/* Terms */}
        <div className="space-y-2.5 pt-1">
          <div className="flex items-start gap-2.5">
            <input
              type="checkbox"
              id="termsAccepted"
              className="w-4 h-4 rounded border-border accent-primary mt-0.5 flex-shrink-0"
              {...register('termsAccepted', { required: 'You must accept the Terms & Conditions' })}
            />
            <label htmlFor="termsAccepted" className="text-xs text-muted-foreground leading-relaxed">
              I agree to the{' '}
              <span className="text-primary font-medium">Terms & Conditions</span>{' '}
              and{' '}
              <span className="text-primary font-medium">Income Disclosure Statement</span>
            </label>
          </div>
          {errors.termsAccepted && <p className="text-xs text-danger">{errors.termsAccepted.message}</p>}

          <div className="flex items-start gap-2.5">
            <input
              type="checkbox"
              id="privacyAccepted"
              className="w-4 h-4 rounded border-border accent-primary mt-0.5 flex-shrink-0"
              {...register('privacyAccepted', { required: 'You must accept the Privacy Policy' })}
            />
            <label htmlFor="privacyAccepted" className="text-xs text-muted-foreground leading-relaxed">
              I have read and agree to the{' '}
              <span className="text-primary font-medium">Privacy Policy</span>
            </label>
          </div>
          {errors.privacyAccepted && <p className="mt-1 text-xs text-danger">{errors.privacyAccepted.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-70 mt-2"
          style={{ minHeight: '48px' }}
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              Creating your account...
            </>
          ) : (
            <>
              Create My Richésse Account
              <Icon name="ArrowRightIcon" size={16} />
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already a member?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:text-primary-dark transition-colors">
          Sign in
        </button>
      </p>
    </div>
  );
}